import React, { useState } from "react";
import { Typography, Button, List, Upload } from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { useRecordingUpload } from "../../useRecordingUpload";
import VideoPlayerModal from "../../../../../../../Common/VideoPlayerModal";

export interface RecordingAttachment {
  id: string;
  name: string;
  type: "video" | "audio";
  fileLink: string;
  file?: File;
}

interface AttachmentsRecordingsProps {
  value?: RecordingAttachment[];
  onChange?: (recordings: RecordingAttachment[]) => void;
  disabled?: boolean;
}

export default function AttachmentsRecordings({
  value = [],
  onChange,
  disabled = false,
}: AttachmentsRecordingsProps): React.ReactElement {
  const { uploadMultiple, uploading } = useRecordingUpload();
  const [videoPlayerOpen, setVideoPlayerOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<RecordingAttachment | null>(
    null,
  );

  const handleFileSelect = async (fileList: File[]) => {
    if (!fileList.length) return;
    const uploaded = await uploadMultiple(fileList);
    const updated = [...value, ...uploaded];
    onChange?.(updated);
  };

  const handleRemove = (id: string) => {
    const updated = value.filter((r) => r.id !== id);
    onChange?.(updated);
  };

  const handleOpenPlayer = (item: RecordingAttachment) => {
    setCurrentVideo(item);
    setVideoPlayerOpen(true);
  };

  const formatSize = (bytes?: number) => {
    if (!bytes) return "";
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <>
      <div
        style={{
          border: "1px solid #e8e8e8",
          borderRadius: 8,
          padding: 16,
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: value.length > 0 ? 12 : 0,
          }}
        >
          <div>
            <Typography.Text strong>Attachments (Recordings)</Typography.Text>
            <br />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Supported formats: MP4, MP3, WAV
            </Typography.Text>
          </div>

          <Upload
            accept=".mp4,.mp3,.wav,.m4a"
            multiple
            showUploadList={false}
            beforeUpload={(file, fileList) => {
              // Ant Design calls beforeUpload once per file, passing the full
              // fileList each time. Only handle the batch on the last file so
              // we don't upload every file once per selected file.
              if (file === fileList[fileList.length - 1]) {
                handleFileSelect(fileList);
              }
              return false;
            }}
            disabled={disabled || uploading}
          >
            <Button
              icon={<UploadOutlined />}
              type="primary"
              ghost
              loading={uploading}
              disabled={disabled}
            >
              {uploading ? "Uploading..." : "Upload from Computer"}
            </Button>
          </Upload>
        </div>

        {value.length > 0 && (
          <div
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              padding: 16,
              marginTop: 12,
            }}
          >
            <Typography.Text style={{ marginBottom: 12, display: "block" }}>
              {value.length} file{value.length !== 1 ? "s" : ""} attached
            </Typography.Text>

            <List
              dataSource={value}
              renderItem={(item) => (
                <List.Item
                  style={{
                    padding: "8px 0",
                    borderBottom: "1px solid #f0f0f0",
                  }}
                  actions={[
                    <Button
                      key="delete"
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemove(item.id)}
                      disabled={disabled}
                      style={{ color: "#999" }}
                    />,
                  ]}
                >
                  <div>
                    <Typography.Link
                      onClick={() => handleOpenPlayer(item)}
                      style={{
                        textDecoration: "underline",
                        color: "#000000D9",
                      }}
                    >
                      {item.name}
                    </Typography.Link>
                    {item.file?.size && (
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 12, marginLeft: 8 }}
                      >
                        ({formatSize(item.file.size)})
                      </Typography.Text>
                    )}
                  </div>
                </List.Item>
              )}
            />
          </div>
        )}
      </div>

      <VideoPlayerModal
        open={videoPlayerOpen}
        onClose={() => {
          setVideoPlayerOpen(false);
          setCurrentVideo(null);
        }}
        videoName={currentVideo?.name}
        fileLink={currentVideo?.fileLink}
      />
    </>
  );
}
